<script lang="ts">
    import { createEventDispatcher } from 'svelte';

    export let page: number = 1;
    export let pageSize: number = 20;
    export let totalCount: number = 0;
    export let pageSizeOptions: number[] = [20, 40, 80];

    const dispatch = createEventDispatcher<{
        pageChange: number;
        pageSizeChange: number;
    }>();

    $: totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    $: page = Math.min(page, totalPages);
    $: visiblePages = buildPageList(page, totalPages);

    function buildPageList(current: number, total: number): (number | '…')[] {
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const pages: (number | '…')[] = [1];
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        if (start > 2) pages.push('…');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < total - 1) pages.push('…');
        pages.push(total);
        return pages;
    }

    function goto(p: number) {
        if (p < 1 || p > totalPages || p === page) return;
        page = p;
        dispatch('pageChange', page);
    }

    function onSizeChange(e: Event) {
        const size = Number((e.target as HTMLSelectElement).value);
        pageSize = size;
        page = 1;
        dispatch('pageSizeChange', size);
    }

    $: firstItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
    $: lastItem = Math.min(page * pageSize, totalCount);
</script>

{#if totalCount > 0}
    <div class="pager">
        <div class="pager-left">
            <span class="range">Showing {firstItem}–{lastItem} of {totalCount}</span>
        </div>

        <div class="pager-controls" role="navigation" aria-label="Pagination">
            <button
                type="button"
                class="nav-btn"
                aria-label="Previous page"
                disabled={page <= 1}
                on:click={() => goto(page - 1)}
            ><i class="angle left icon" aria-hidden="true"></i></button>

            {#each visiblePages as p}
                {#if p === '…'}
                    <span class="ellipsis" aria-hidden="true">…</span>
                {:else}
                    <button
                        type="button"
                        class="page-btn"
                        class:active={p === page}
                        aria-current={p === page ? 'page' : undefined}
                        on:click={() => goto(p)}
                    >{p}</button>
                {/if}
            {/each}

            <button
                type="button"
                class="nav-btn"
                aria-label="Next page"
                disabled={page >= totalPages}
                on:click={() => goto(page + 1)}
            ><i class="angle right icon" aria-hidden="true"></i></button>
        </div>

        <div class="pager-right">
            <label class="size-label" for="page-size">Per page</label>
            <select id="page-size" class="size-select" value={pageSize} on:change={onSizeChange}>
                {#each pageSizeOptions as s}
                    <option value={s}>{s}</option>
                {/each}
            </select>
        </div>
    </div>
{/if}

<style>
    .pager {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
        margin-top: var(--space-5);
        padding-top: var(--space-4);
        border-top: 1px solid var(--greyish);
        flex-wrap: wrap;
    }

    .pager-left,
    .pager-right {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .range { font-variant-numeric: tabular-nums; }

    .pager-controls {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
    }

    .nav-btn,
    .page-btn {
        min-width: 2rem;
        height: 2rem;
        padding: 0 .6rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-input);
        background: var(--surface);
        color: var(--grey);
        font: inherit;
        font-size: var(--text-sm);
        font-variant-numeric: tabular-nums;
        cursor: pointer;
        transition: background .12s, border-color .12s, color .12s;
    }

    .nav-btn :global(i.icon) { margin: 0; }

    .nav-btn:hover:not(:disabled),
    .page-btn:hover:not(.active) {
        background: var(--surface-hover);
        border-color: var(--primary);
    }

    .nav-btn:disabled {
        opacity: .4;
        cursor: not-allowed;
    }

    .page-btn.active {
        background: var(--primary);
        border-color: var(--primary);
        color: white;
        font-weight: 600;
    }

    .nav-btn:focus-visible,
    .page-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .ellipsis {
        padding: 0 var(--space-1);
        color: var(--grey-secondary);
    }

    .size-label { font-size: var(--text-sm); color: var(--grey-secondary); }

    .size-select {
        padding: var(--space-1) var(--space-2);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-input);
        background: var(--surface);
        color: var(--grey);
        font: inherit;
        font-size: var(--text-sm);
    }

    .size-select:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: var(--focus-ring);
    }

    @media (max-width: 640px) {
        .pager { justify-content: center; }
        .pager-left,
        .pager-right { flex-basis: 100%; justify-content: center; }
    }
</style>
